import React, { ReactElement, ReactNode } from 'react';
import Helmet, { HelmetProps } from 'react-helmet';
import { graphql, StaticQuery } from 'gatsby';
import { WindowLocation } from '@reach/router';

import { ErrorBoundary } from 'src/components/Common';

import Footer from './Partials/_Footer';
import Header from './Partials/_Header';
import RemovePreloader from './Partials/_RemovePreloader';

interface DefaultLayoutProps {
  /** layout content */
  children: ReactNode;
  /** location prop from page */
  location: WindowLocation<unknown>;
}

const DefaultLayout = ({
  children,
  location,
}: DefaultLayoutProps): ReactElement => (
  <ErrorBoundary>
    <StaticQuery
      query={graphql`
        query DefaultLayoutQuery {
          site {
            buildTime
            siteMetadata {
              buildVersion
              title
              packageVersion
            }
          }
          # used to get current page data through location
          allPages: allSitePage {
            edges {
              node {
                path
                pageContext
              }
            }
          }
        }
      `}
      render={(data) => {
        const menuPages = data.allPages.edges
          .filter(({ node }) => node.pageContext.frontmatter?.showInMenu)
          .sort((a, b) =>
            a.node.pageContext.frontmatter.sorting >
            b.node.pageContext.frontmatter.sorting
              ? 1
              : -1,
          )
          .map(({ node }) => ({
            path: node.path,
            title: node.pageContext.frontmatter?.title || 'no title',
          }));

        let thisPage;
        try {
          thisPage = data.allPages.edges.find(
            ({ node }) => node.path === location.pathname,
          ).node;
        } catch (e) {
          thisPage = data.allPages.edges.find(
            ({ node }) => node.path === '/404/',
          ).node;
        }

        // #region helmetProps sammeln
        // TODO: Add open graph
        // Metadaten generieren
        const metadata = [];
        if (thisPage.pageContext.frontmatter.metaDescription) {
          metadata.push({
            name: 'description',
            content: thisPage.pageContext.frontmatter.metaDescription,
          });
        }

        const helmetProps: HelmetProps = {
          htmlAttributes: {
            lang: 'de',
          },
          meta: metadata,
        };

        // Seitentitel generieren
        if (thisPage.pageContext.frontmatter.browserTitle) {
          helmetProps.title = thisPage.pageContext.frontmatter.browserTitle;
        } else {
          helmetProps.title = thisPage.pageContext.frontmatter.title;
          helmetProps.titleTemplate = `%s | ${data.site.siteMetadata.title}`;
        }
        // #endregion

        return (
          <div className="layout-default">
            <Helmet {...helmetProps} />
            {/* try to remove preloader on every update */}
            <RemovePreloader update={Math.random()} />
            <Header
              location={location}
              pages={menuPages}
              siteTitle={data.site.siteMetadata.title}
            />
            <section id="main-content">{children}</section>
            <Footer
              siteBuildTime={data.site.buildTime}
              siteBuildVersion={data.site.siteMetadata.buildVersion}
              sitePackageVersion={data.site.siteMetadata.packageVersion}
              siteTitle={data.site.siteMetadata.title}
            />
          </div>
        );
      }}
    />
  </ErrorBoundary>
);

export default DefaultLayout;
